import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Stepper from "./components/Stepper";
import StepperControl from "./components/StepperControl";
import AgentDetails from "./components/steps/AgentDetails";
import KnowledgeBase from "./components/steps/KnowledgeBase";
import ChooseWorkflow from "./components/steps/ChooseWorkflow";
import Finish from "./components/steps/Finish";
import Card from "components/card";
import { NewAgentProvider, useNewAgent } from "./contexts/NewAgentContext";
import IntegrationStep from "./components/steps/Integration";
import AssignSpecialistsStep from "./components/steps/AssignSpecialistsStep";
import Trigger from "./components/steps/Trigger";
import Pipeline from "./components/steps/Pipeline";
import { CREATE_AGENT_CATEGORIES, AGENT_TYPES } from "constants/agents";
import { useTranslation } from "hooks/useTranslation";

const AgentNew = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const { formData, setFormData } = useNewAgent();

  const agentCategory = searchParams.get("category") as CREATE_AGENT_CATEGORIES | null;
  const isOrchestrator = agentCategory === CREATE_AGENT_CATEGORIES.ORCHESTRATOR;
  // For FUNCTIONAL, agent_type will be selected in AgentDetails step, so check both URL and formData
  const isFunctional = agentCategory === CREATE_AGENT_CATEGORIES.FUNCTIONAL || formData.agent_type === AGENT_TYPES.AUTONOMOUS;
  const { t } = useTranslation();

  const steps = useMemo(() => {
    if (isOrchestrator) {
      // Orchestrator steps: Agent Details -> Choose Workflow -> Assign Specialists -> Finish
      return [
        { stepNo: 1, name: t("agents.newAgent.stepAgentDetails") },
        { stepNo: 2, name: t("agents.newAgent.stepChooseWorkflow") },
        { stepNo: 3, name: t("agents.assignedSpecialists.assignSpecialists") },
        { stepNo: 4, name: t("agents.newAgent.stepFinish") },
      ];
    }

    if (isFunctional) {
      // Functional steps: Agent Details -> Trigger -> Pipeline -> Finish
      return [
        { stepNo: 1, name: t("agents.newAgent.stepAgentDetails") },
        { stepNo: 2, name: t("agents.newAgent.stepTrigger") },
        { stepNo: 3, name: t("agents.newAgent.stepPipeline") },
        { stepNo: 4, name: t("agents.newAgent.stepFinish") },
      ];
    }

    // Specialist steps: Agent Details -> Knowledge Base -> Choose Workflow -> Integration -> Finish
    return [
      { stepNo: 1, name: t("agents.newAgent.stepAgentDetails") },
      { stepNo: 2, name: t("agents.newAgent.stepKnowledgeBase") },
      { stepNo: 3, name: t("agents.newAgent.stepChooseWorkflow") },
      { stepNo: 4, name: t("agents.newAgent.stepIntegration") },
      { stepNo: 5, name: t("agents.newAgent.stepFinish") },
    ];
  }, [isOrchestrator, isFunctional, t]);

  // Reset to step 1 when agent type changes
  useEffect(() => {
    setCurrentStep(1);
  }, [isOrchestrator, isFunctional]);

  const displayStep = (step: {
    stepNo: number;
    name: string;
    highlighted?: boolean;
    selected?: boolean;
    completed?: boolean;
  }) => {
    if (isOrchestrator) {
      // Orchestrator flow: Agent Details -> Choose Workflow -> Assign Specialists -> Finish
      switch (step.stepNo) {
        case 1:
          return <AgentDetails />;
        case 2:
          return <ChooseWorkflow />;
        case 3:
          return (
            <AssignSpecialistsStep
              formData={formData}
              setFormData={setFormData}
            />
          );
        case 4:
          return <Finish />;
        default:
          return null;
      }
    } else if (isFunctional) {
      // Functional flow: Agent Details -> Trigger -> Pipeline -> Finish
      switch (step.stepNo) {
        case 1:
          return <AgentDetails />;
        case 2:
          return <Trigger />;
        case 3:
          return <Pipeline />;
        case 4:
          return <Finish />;
        default:
          return null;
      }
    } else {
      // Specialist flow: Agent Details -> Knowledge Base -> Choose Workflow -> Integration -> Finish
      switch (step.stepNo) {
        case 1:
          return <AgentDetails />;
        case 2:
          return <KnowledgeBase />;
        case 3:
          return <ChooseWorkflow />;
        case 4:
          return (
            <IntegrationStep formData={formData} setFormData={setFormData} />
          );
        case 5:
          return <Finish />;
        default:
          return null;
      }
    }
  };

  const handleClick = (direction: string) => {
    let newStep = currentStep;

    direction === "next" ? newStep++ : newStep--;
    // check if steps are within bounds
    newStep > 0 && newStep <= steps.length && setCurrentStep(newStep);
  };
  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[300px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 dark:from-brand-700 dark:to-brand-900 md:h-[320px]" />
      <div className="w-md:2/3 mx-auto h-full w-5/6 md:px-3  3xl:w-7/12">
        <div className="-mt-[280px] w-full pb-10 md:-mt-[240px] md:px-[70px]">
          <Stepper
            action={setCurrentStep}
            steps={steps}
            currentStep={currentStep}
          />
        </div>
        <Card extra={"h-full mx-auto pb-3 dark:bg-navy-800"}>
          <div className="rounded-[20px] dark:bg-navy-700">
            {displayStep(steps[currentStep - 1])}
          </div>
          {/* navigation button */}
          <StepperControl
            handleClick={handleClick}
            currentStep={currentStep}
            steps={steps}
          />
        </Card>
      </div>
    </div>
  );
};

const AgentNewWrapper = () => (
  <NewAgentProvider>
    <AgentNew />
  </NewAgentProvider>
);

export default AgentNewWrapper;
